<script>
  import { onMount } from 'svelte';
  import { COLORS, PADDING, buildScales, clearCanvas, drawGrid } from './chartUtils.js';
  import { formatSignedPercent } from '../../../lib/strategyResult.js';

  // Geometric annual returns: [{ year, value }] (fraction). Bar chart on an index (year) axis â the only
  // new-grammar piece vs the time-axis charts: year labels instead of dates.
  export let data = [];

  let canvas;
  let ctx;
  let container;
  let width = 600;
  let height = 200;
  let hoverIndex = -1;

  const fmt = (v) => formatSignedPercent(v);
  // Year-keyed points already carry { year, value } â buildScales reads .value, the bars read .year.
  $: series = data || [];
  $: hasData = series.length >= 1;
  $: summary = hasData
    ? `Annual returns: ${series.map((p) => `${p.year} ${fmt(p.value)}`).join(', ')}.`
    : 'Annual returns: insufficient data.';

  function resize() {
    if (!container) return;
    width = Math.max(200, container.offsetWidth);
    height = 200;
  }

  function draw() {
    if (!ctx || !hasData) return;
    clearCanvas(ctx, width, height);
    // Symmetric-ish range that always includes zero so positive/negative bars read correctly.
    let mag = 0;
    for (const p of series) mag = Math.max(mag, Math.abs(p.value));
    if (mag === 0) mag = 0.01;
    const scales = buildScales(series, width, height, { valueMin: -mag, valueMax: mag });
    drawGrid(ctx, width, height, scales, fmt);

    const zeroY = scales.yOf(0);
    const plotW = width - PADDING.left - PADDING.right;
    const slot = plotW / series.length;
    const barW = Math.max(2, slot * 0.6);

    ctx.font = '10px sans-serif';
    ctx.textAlign = 'center';
    series.forEach((p, i) => {
      const cx = PADDING.left + slot * (i + 0.5);
      const y = scales.yOf(p.value);
      ctx.fillStyle = p.value >= 0 ? COLORS.gain : COLORS.loss;
      const top = Math.min(y, zeroY);
      const h = Math.max(1, Math.abs(y - zeroY));
      ctx.fillRect(cx - barW / 2, top, barW, h);

      // Year label under the axis.
      ctx.fillStyle = COLORS.label;
      ctx.fillText(String(p.year), cx, height - 6);

      // Highlight the hovered bar's value above/below it.
      if (i === hoverIndex) {
        ctx.fillStyle = '#e8e9ea';
        const labelY = p.value >= 0 ? top - 4 : top + h + 12;
        ctx.fillText(fmt(p.value), cx, labelY);
      }
    });

    // Zero baseline.
    ctx.strokeStyle = COLORS.label;
    ctx.globalAlpha = 0.5;
    ctx.beginPath();
    ctx.moveTo(PADDING.left, zeroY);
    ctx.lineTo(width - PADDING.right, zeroY);
    ctx.stroke();
    ctx.globalAlpha = 1;
  }

  $: if (ctx && canvas && hasData && width && height && series && hoverIndex >= -1) draw();

  function onMove(e) {
    if (!hasData) return;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const plotW = width - PADDING.left - PADDING.right;
    const slot = plotW / series.length;
    hoverIndex = Math.max(0, Math.min(series.length - 1, Math.floor((x - PADDING.left) / slot)));
  }
  function onLeave() { hoverIndex = -1; }

  let liveMsg = '';
  function onKey(e) {
    if (!hasData) return;
    if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
      e.preventDefault();
      const start = hoverIndex < 0 ? 0 : hoverIndex;
      hoverIndex = Math.max(0, Math.min(series.length - 1, start + (e.key === 'ArrowRight' ? 1 : -1)));
      const p = series[hoverIndex];
      liveMsg = `${p.year}: ${fmt(p.value)}`;
    }
  }

  // Lazy 2D-context acquisition: the canvas renders inside {#if hasData}, so it may not exist at
  // mount (empty data — acquiring unconditionally threw and broke the whole reactive graph) and may
  // APPEAR later when data arrives. Acquire the context whenever the canvas binds.
  $: if (canvas && !ctx) { ctx = canvas.getContext('2d'); resize(); }

  onMount(() => {
    if (!canvas) return; // not rendered (empty state) — nothing to wire up
    if (canvas) ctx = canvas.getContext('2d');
    resize();
    draw();
    const onResize = () => { resize(); draw(); };
    window.addEventListener('resize', onResize);
    let pendingDraw;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && ctx && canvas) {
          clearTimeout(pendingDraw);
          pendingDraw = setTimeout(() => { resize(); draw(); }, 50);
        }
      });
    }, { threshold: 0.1 });
    if (container) observer.observe(container);
    return () => { window.removeEventListener('resize', onResize); observer.disconnect(); clearTimeout(pendingDraw); };
  });
</script>

<div class="bt-chart" bind:this={container}>
  <h4 class="bt-chart-title">Annual Returns</h4>
  {#if hasData}
    <canvas
      bind:this={canvas}
      width={width}
      height={height}
      tabindex="0"
      aria-label={summary}
      on:mousemove={onMove}
      on:mouseleave={onLeave}
      on:keydown={onKey}
    ></canvas>
    <div class="bt-chart-live" aria-live="polite">{liveMsg}</div>
    <details class="bt-chart-data">
      <summary>View data as table</summary>
      <table>
        <thead><tr><th>Year</th><th>Return</th></tr></thead>
        <tbody>
          {#each series as p}<tr><td>{p.year}</td><td>{fmt(p.value)}</td></tr>{/each}
        </tbody>
      </table>
    </details>
  {:else}
    <div class="empty-state"><p class="empty-state-description">Insufficient data for this chart.</p></div>
  {/if}
</div>

<style>
  @import './charts.css';
</style>
